import alertService from '@jetbrains/ring-ui/components/alert-service/alert-service'
import type {ButtonAttrs} from '@jetbrains/ring-ui/components/button/button'
import Button from '@jetbrains/ring-ui/components/button/button'
import {useState} from 'react'
import {graphql, useMutation} from 'react-relay'
import {useLocation, useNavigate} from 'react-router-dom'

import {extractErrorFromRelay} from '../../../../../relay/extractErrorFromRelay'
import Routes, {getHref} from '../../../../../routes'
import {noop} from '../../../../../utils/empty'
import {usePipelineId} from '../../hooks/pipeline'
import {serializeError} from '../../utils/error'

import type {
  RunButtonMutation,
  RunButtonMutation$data,
} from './__generated__/RunButtonMutation.graphql'

type Props = ButtonAttrs & {
  headId: string
  beforeRun?: () => Promise<boolean>
  afterRun?: () => unknown
  pipelineId?: string
  branchName?: string | null
}

const mutation = graphql`
  mutation RunButtonMutation($build: BuildInput!) {
    addBuildToQueue(input: $build) {
      rawId
    }
  }
`

const booleanNoop = () => Promise.resolve(true)

export default function RunButton({
  headId,
  beforeRun = booleanNoop,
  afterRun = noop,
  children = 'Run',
  pipelineId,
  branchName,
  ...restProps
}: Props) {
  const [isLoading, setIsLoading] = useState(false)
  const [addBuildToQueue] = useMutation<RunButtonMutation>(mutation)
  const pipelineIdFromUrl = usePipelineId()
  const navigate = useNavigate()
  const location = useLocation()

  return (
    <Button
      onClick={async () => {
        setIsLoading(true)
        try {
          const isValid = await beforeRun()
          if (isValid) {
            const data = await new Promise<RunButtonMutation$data>((resolve, reject) =>
              addBuildToQueue({
                variables: {
                  build: {
                    buildType: {id: headId},
                    branchName,
                  },
                },
                onCompleted: resolve,
                onError: reject,
              }),
            )
            const triggeredId = data.addBuildToQueue?.rawId
            afterRun()
            navigate(
              getHref(Routes.PIPELINE_RUN, {
                pipelineId: pipelineId ?? pipelineIdFromUrl,
                pipelineRunId: String(triggeredId),
              }),
              {
                state: {
                  isCreated: location?.state?.isCreated,
                },
              },
            )
          }
        } catch (e) {
          if (e instanceof Error) {
            alertService.error(serializeError(extractErrorFromRelay(e)))
          }
        } finally {
          setIsLoading(false)
        }
      }}
      loader={isLoading}
      {...restProps}
    >
      {children}
    </Button>
  )
}
